// payments.js
// This module provides functions to setup and manage Stripe payment elements.

import { loadStripe } from '@stripe/stripe-js/pure';

let stripePromise = null;

async function getStripe() {
  if (!stripePromise) {
    try {
      console.log('Loading Stripe...')
      stripePromise = await loadStripe(STRIPE_PUBLIC_KEY);
      if (!stripePromise) {
        console.error('Stripe failed to initialize: loadStripe returned null or undefined');
      }
    } catch (error) {
      console.error('Error initializing Stripe:', error);
    }
  }
  return stripePromise;
}

export const RETURN_URL = `${SERVER_URL_BASE}/cart/success/`

/**
 * Generates the appearance object for the Stripe elements.
 * @returns {Object} The appearance settings for Stripe elements.
 */
function getAppearance() {
  const computedStyle = getComputedStyle(document.documentElement)
  return {
    theme: 'stripe',
    variables: {
      colorPrimary: computedStyle.getPropertyValue('--bs-primary').trim(),
      colorBackground: '#ffffff',
      colorText: computedStyle.getPropertyValue('--bs-body-color').trim(),
      colorDanger: computedStyle.getPropertyValue('--bs-danger').trim(),
      fontFamily: computedStyle.getPropertyValue('--bs-font-sans-serif').trim(),
      spacingUnit: '3px',
      borderRadius: computedStyle.getPropertyValue('--bs-border-radius').trim(),
      fontSizeBase: computedStyle.getPropertyValue('--bs-body-font-size'),
      fontLineHeight: computedStyle.getPropertyValue('--bs-body-line-height')
    }
  }
}

/**
 * Sets up the Stripe payment element.
 *
 * @param {Object} initObj - The initialization object which contains: divId - The ID of the div where the element will be mounted. formId - The ID of the form associated with the payment element. clientSecret - The client secret for the payment intent.
 * @param {Object} customerDefaults - The default values for the payment element.
 * @param {Object} cartStore - The cart store instance
 * @returns {Promise} - A promise that resolves to the payment element.
 */
export async function setupStripePaymentElement(initObj, customerDefaults, cartStore) {
  const stripe = await getStripe()
  if (!stripe) {
    console.error('Stripe failed to initialize')
    return
  }

  const appearance = getAppearance()
  const options = {
    loader: 'auto',
    appearance,
    clientSecret: initObj.clientSecret
  }

  const elements = stripe.elements(options)

  addExpressCheckout(elements, initObj.clientSecret, cartStore)

  const paymentElement = elements.create('payment', {
    layout: {
      type: 'accordion',
      defaultCollapsed: false,
      radios: true,
      spacedAccordionItems: false
    },
    defaultValues: customerDefaults
  })
  paymentElement.mount(`#${initObj.divId}`)
  addStripeEventListener(elements, initObj.formId, cartStore)
  return paymentElement
}

/**
 * Initializes a Stripe checkout session and mounts it in a given div.
 * @param {string} clientSecret - The client secret for the checkout session.
 * @param {string} divId - The ID of the div where the checkout will be mounted.
 * @returns {Promise} A promise that resolves to the checkout object.
 */
export async function setupStripeCheckout(clientSecret, divId) {
  const stripe = await getStripe()
  const checkout = await stripe.initEmbeddedCheckout({
    clientSecret
  })
  checkout.mount(`#${divId}`)
  return checkout
}

/**
 * Adds a submit event listener to a form to handle Stripe payment confirmation.
 * @param {Object} elements - The Stripe elements instance used to create the Payment Element.
 * @param {string} formId - The ID of the form to attach the event listener to.
 * @param {Object} cartStore - The cart store instance
 */
export function addStripeEventListener(elements, formId, cartStore) {
  const form = document.getElementById(formId)

  form.addEventListener('submit', async (event) => {
    event.preventDefault()
    const stripe = await getStripe()
    cartStore.setIsLoading(true)
    const { error, paymentIntent } = await stripe.confirmPayment({
      elements,
      confirmParams: {
        return_url: RETURN_URL
      }
    })

    if (error) {
      cartStore.setIsLoading(false)
      const messageContainer = document.querySelector('#error-message')
      messageContainer.classList.add('alert', 'alert-soft-danger')
      messageContainer.textContent = error.message
    } else {
      // Clear client secret on successful payment
      cartStore.setStripeClientSecret(null)
    }
  })
}

/**
 * Adds express checkout functionality to the cart.
 * @param {object} elements - The elements object used for creating the express checkout element.
 * @param {string} clientSecret - The client secret for the payment intent.
 * @param {Object} cartStore - The cart store instance
 */
export async function addExpressCheckout(elements, clientSecret, cartStore) {
  const stripe = await getStripe()
  const expressCheckoutOptions = {
    buttonType: {
      applePay: 'plain',
      googlePay: 'plain',
      paypal: 'paypal'
    },
    buttonHeight: 45,
    buttonTheme: {
      applePay: 'black',
      googlePay: 'black',
      paypal: 'black'
    }
  }
  const expressCheckoutElement = elements.create(
    'expressCheckout',
    expressCheckoutOptions
  )
  expressCheckoutElement.mount('#express-checkout-element')
  expressCheckoutElement.on('click', (event) => {
    const options = {
      emailRequired: true
    }
    event.resolve(options)
  })

  expressCheckoutElement.on('confirm', async (event) => {
    const { error: submitError } = await elements.submit()

    if (submitError) {
      handleExpressError(submitError)
      return
    }

    const { error } = await stripe.confirmPayment({
      elements,
      clientSecret,
      confirmParams: {
        return_url: RETURN_URL
      }
    })

    if (error) {
      // This point is only reached if there's an immediate error when
      // confirming the payment. Show the error to your customer (for example, payment details incomplete)
      handleExpressError(error)
    } else {
      // Clear client secret on successful payment
      cartStore.setStripeClientSecret(null)
      // The payment UI automatically closes with a success animation.
      // Your customer is redirected to your `return_url`.
    }
  })
}

const handleExpressError = (error) => {
  const messageContainer = document.querySelector('#express-error-message')
  messageContainer.textContent = error.message
}

/**
 * Unmounts all active Stripe components.
 */
export function unmountStripeComponents() {
  if (window.expressCheckoutElement) {
    window.expressCheckoutElement.unmount();
    window.expressCheckoutElement = null;
  }
  if (window.paymentElement) {
    window.paymentElement.unmount();
    window.paymentElement = null;
  }
  if (window.stripeElements) {
    window.stripeElements = null;
  }
}

/**
 * Confirms a payment intent using an existing payment method
 * @param {string} clientSecret - The client secret of the payment intent
 * @param {string} paymentMethodId - The ID of the payment method to use
 * @param {Object} cartStore - The cart store instance
 * @returns {Promise} A promise that resolves with the confirmation result
 */
export async function confirmPaymentWithExistingMethod(clientSecret, paymentMethodId, cartStore) {
  const stripe = await getStripe()
  if (!stripe) {
    throw new Error('Stripe failed to initialize')
  }

  const result = await stripe.confirmPayment({
    clientSecret,
    confirmParams: {
      payment_method: paymentMethodId,
      return_url: RETURN_URL
    }
  })

  // If payment was successful (no error), clear the client secret
  if (!result.error) {
    cartStore.setStripeClientSecret(null)
  }

  return result
}

// Export an object containing all the functions for ease of import.
export const Payments = {
  RETURN_URL,
  getAppearance,
  setupStripePaymentElement,
  setupStripeCheckout,
  addStripeEventListener,
  getStripe,
  unmountStripeComponents,
  confirmPaymentWithExistingMethod
}